/* eslint-disable formatjs/no-literal-string-in-jsx */
/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button } from '../../components/Button/Button'
import { Section } from '../../components/Section'
import { useRoutes } from '../../routes'
import { MEDIUM_BREAKPOINT, SMALL_BREAKPOINT } from '../../utils/breakpoints'

export type Testimonial = {
  background: string
  textColor: string
  quote: string
  name: string
  role: string
  logoUrl: string
  link?: string
}

export const VANMOOF_LOGO =
  'https://cdn.sanity.io/images/dnsgqii5/production/1233da015c3f54d4b39398cf23bff77788db43db-225x225.png'
export const AGORAPULSE_LOGO =
  'https://cdn.sanity.io/images/dnsgqii5/production/0bf8633a236c6cac7713394c509e0db9c1a12590-225x225.png'
export const PREMIUM_PLUS_LOGO =
  'https://cdn.sanity.io/images/dnsgqii5/production/085fefb9f8bfb91feaa37bb2aceb52c1618e61a0-160x160.png'

export function SectionHomeSocialProof({
  testimonials: testimonialsOverride,
  tagline,
  hideCta,
}: {
  testimonials?: Testimonial[]
  tagline?: string
  hideCta?: boolean
} = {}) {
  const intl = useIntl()
  const routes = useRoutes()

  const vanmoofRole = intl.formatMessage({
    id: 'home.socialProof.vanmoof.role',
    defaultMessage: 'Digital Manager at VanMoof',
  })
  const agorapulseRole = intl.formatMessage({
    id: 'home.socialProof.agorapulse.role',
    defaultMessage: 'Principal Product at Agorapulse',
  })
  const premiumPlusRole = intl.formatMessage({
    id: 'home.socialProof.premiumPlus.role',
    defaultMessage: 'COO at Premium Plus',
  })

  const agorapulseQuote = intl.formatMessage({
    id: 'home.socialProof.agorapulse.quote',
    defaultMessage:
      'The number of questions has dropped 10x, and everyone is able to get the information they need.',
  })
  const premiumPlusQuote = intl.formatMessage({
    id: 'home.socialProof.premiumPlus.quote',
    defaultMessage:
      "Slite's interface is straightforward. You don't need training to use it.",
  })

  const testimonials: Testimonial[] = testimonialsOverride ?? [
    {
      background: 'blue-45',
      textColor: 'star-white',
      quote: intl.formatMessage({
        id: 'home.socialProof.vanmoof.quote',
        defaultMessage:
          'Now my team and its knowledge are walking hand in hand',
      }),
      name: 'Martijn Hazelaar',
      role: vanmoofRole,
      logoUrl: VANMOOF_LOGO,
      link: routes.customers.detail({ slug: 'vanmoof' }),
    },
    {
      background: 'saturn-orange',
      textColor: 'star-white',
      quote: agorapulseQuote,
      name: 'Alexis Dupont',
      role: agorapulseRole,
      logoUrl: AGORAPULSE_LOGO,
      link: routes.customers.detail({ slug: 'agorapulse' }),
    },
    // Placeholder — copy lands later; left as a duplicate of card 2 per design.
    {
      background: 'moon-dust',
      textColor: 'shade-00',
      quote: premiumPlusQuote,
      name: "Thomas D'Hoe",
      role: premiumPlusRole,
      logoUrl: PREMIUM_PLUS_LOGO,
      link: routes.customers.detail({ slug: 'premium-plus' }),
    },
  ]

  return (
    <Section background="moon-dust-lighter" containerVerticalPadding={120}>
      <div
        className="grid-12 grid-gap-24 grid-gap-12-small"
        css={css`
          @media (min-width: ${SMALL_BREAKPOINT}px) {
            grid-auto-rows: 1fr;
          }
        `}
      >
        {testimonials.map((t, i) => {
          const CardTag = t.link ? 'a' : 'div'
          return (
            <CardTag
              key={`${t.name}-${i}`}
              className={classNames(
                'grid-span-4-large grid-span-12-medium grid-span-12-small',
                'radius-larger',
                'flexbox flexbox-column flexbox-space-between'
              )}
              href={t.link}
              css={css`
              background: var(--${t.background});
              color: var(--${t.textColor});
              padding: 32px;
              min-height: 460px;
              transition: transform 0.25s cubic-bezier(0.215, 0.61, 0.355, 1),
                box-shadow 0.25s cubic-bezier(0.215, 0.61, 0.355, 1);
              &:hover {
                transform: translateY(${t.link ? '-6px' : '0'});
              }
              @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
                padding: 24px;
                min-height: 320px;
              }
            `}
            >
              <p className="font-family-title font-size-3 line-height-large">
                {`“${t.quote}”`}
              </p>
              <div className="flexbox flexbox-align-center flexbox-column-gap-12 margin-top-40">
                <img
                  src={t.logoUrl}
                  alt=""
                  width={40}
                  height={40}
                  className="radius-round"
                />
                <div>
                  <div className="font-weight-semibold">{t.name}</div>
                  <div className="font-size-7" style={{ opacity: 0.8 }}>
                    {t.role}
                  </div>
                </div>
              </div>
            </CardTag>
          )
        })}
      </div>

      <div
        className="margin-top-40"
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          gap: 24px;
          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
            flex-direction: column;
            align-items: flex-start;
          }
        `}
      >
        <p className="font-size-6 shade-10">
          {tagline ?? (
            <FormattedMessage
              id="home.socialProof.tagline"
              defaultMessage="1,700+ leading companies trust Slite as their single source of truth."
            />
          )}
        </p>
        {!hideCta && (
          <Button
            variant="secondary"
            href={routes.customers.index()}
            withChevron
          >
            <FormattedMessage
              id="home.socialProof.cta"
              defaultMessage="Customer stories"
            />
          </Button>
        )}
      </div>
    </Section>
  )
}
